import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IState } from 'models/reducers/i-state.interface';

import {
  fetchTopOffersAction,
  fetchTopOffersAssetsAction,
  refreshTopOffersUpdateTokenAction,
  resetTopOffersDataAction,
} from 'actions/top-offers.actions';

import SpecialOffersLayout from 'components/specialOffersLayout/SpecialOffersLayout';

import { useTopOffersEvents } from 'hooks/events/useTopOffersEvents';
import { useLoadingTime } from 'hooks/useLoadingTime';

import TopOffersNftsSection from './topOffersNftsSection/TopOffersNftsSection';

const TopOffers = () => {
  const dispatch = useDispatch();
  const { offers, offersAssets, offersLoading, offersValidUntil, updateToken } = useSelector(
    (state: IState) => state.topOffers
  );

  const dataAvailable = !!offers.length && !!offersAssets.length;
  const dataNotAvailable = !offers?.length && !offersAssets.length;

  const { loadingTime, handleStartLoadingTime, handleStopLoadingTime } = useLoadingTime();

  const { sendTopOffersGetEvent } = useTopOffersEvents();

  useEffect(() => {
    offersLoading ? handleStartLoadingTime() : handleStopLoadingTime();
  }, [offersLoading]);

  useEffect(() => {
    dataAvailable && loadingTime && loadingTime > 0 && sendTopOffersGetEvent({ time: loadingTime });
  }, [dataAvailable, loadingTime]);

  useEffect(() => {
    dispatch(fetchTopOffersAction(new AbortController().signal));
  }, [dispatch, updateToken]);

  useEffect(() => {
    if (!offersValidUntil) {
      return;
    }

    const expirationTime = DateTime.fromMillis(offersValidUntil).diffNow().milliseconds;
    const timer = setTimeout(() => {
      dispatch(refreshTopOffersUpdateTokenAction());
    }, expirationTime);

    return () => {
      clearTimeout(timer);
    };
  }, [dispatch, offersValidUntil]);

  useEffect(() => {
    if (offers.length) {
      dispatch(fetchTopOffersAssetsAction(offers.map((offer) => offer.asset_id)));
    }
  }, [dispatch, offers]);

  useEffect(() => {
    return () => {
      dispatch(resetTopOffersDataAction());
    };
  }, [dispatch]);

  return (
    <SpecialOffersLayout
      title="Top offers"
      subtitle="Top deals of all time! Top Offers is where you can acquire the top NFTs in bulk and at large discounts. Resell them for profit in secondary markets or reach the hardest-to-get locations for maximum royalties generation."
      text="Discount to the standard price"
      dataLoading={offersLoading}
      dataAvailable={dataAvailable}
      dataNotAvailable={dataNotAvailable}
      endTimestamp={offersValidUntil ?? 0}
      className="xl:tw-static"
    >
      <TopOffersNftsSection />
    </SpecialOffersLayout>
  );
};

export default TopOffers;
